import { circlePerpendiculars, circleTangents, projectToRadius, QUADRANT_ANGLES } from './circular';
import type { ShapeOps } from './shape-ops';
import { arc } from './arc';
import {
  add,
  angleOf,
  dist,
  fromPolar,
  normalizeDegrees,
  reflectAcross,
  rotateAbout,
  sub,
  vec,
  type Vec2,
} from './vec2';

export interface Circle {
  readonly type: 'circle';
  readonly center: Vec2;
  readonly radius: number;
}

export const circle = (center: Vec2, radius: number): Circle => ({
  type: 'circle',
  center,
  radius,
});

export const CIRCLE_OPS: ShapeOps<Circle> = {
  measurements: (c) => [
    { label: 'Center', kind: 'point', value: c.center },
    { label: 'Radius', kind: 'length', value: c.radius },
    { label: 'Diameter', kind: 'length', value: c.radius * 2 },
    { label: 'Circumference', kind: 'length', value: 2 * Math.PI * c.radius },
    { label: 'Area', kind: 'area', value: Math.PI * c.radius * c.radius },
  ],
  endpoints: () => [],
  midpoint: () => null,
  center: (c) => c.center,
  quadrants: (c) => QUADRANT_ANGLES.map((angle) => fromPolar(c.center, c.radius, angle)),
  closestPoint: (c, p) => projectToRadius(c.center, c.radius, p),
  bounds: (c) => ({
    min: vec(c.center.x - c.radius, c.center.y - c.radius),
    max: vec(c.center.x + c.radius, c.center.y + c.radius),
  }),
  carrier: (c) => ({ kind: 'circle', center: c.center, radius: c.radius }),
  liesOn: (c, p, eps) => Math.abs(dist(c.center, p) - c.radius) <= eps,
  perpendicularFrom: (c, from) => circlePerpendiculars(c.center, c.radius, from),
  tangentFrom: (c, from) => circleTangents(c.center, c.radius, from),
  offset: (c, distance) => (c.radius + distance > 0 ? circle(c.center, c.radius + distance) : null),
  offsetDistance: (c, p) => dist(c.center, p) - c.radius,
  translate: (c, delta) => circle(add(c.center, delta), c.radius),
  rotate: (c, center, degrees) => circle(rotateAbout(c.center, center, degrees), c.radius),
  mirror: (c, a, b) => circle(reflectAcross(c.center, a, b), c.radius),
  parameterOf: (c, p) => angleOf(sub(p, c.center)) / 360,
  period: () => 1,
  piece: (c, t0, t1) =>
    arc(c.center, c.radius, normalizeDegrees(360 * t0), normalizeDegrees(360 * t1)),
};
